import { useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { err, fmt } from "./mpUtils";
import { Airplane, Trash } from "@phosphor-icons/react";

/** Contractor: block out holiday / fully-booked weeks. Tenants see "Away until …" on the card and detail. */
export function AwayDates({ contractor, onSaved }) {
  const [f, setF] = useState({ from: "", to: "", note: "" });
  const [busy, setBusy] = useState(false);
  const periods = contractor.away || [];
  const add = async () => {
    setBusy(true);
    try { const { data } = await API.post("/marketplace/me/away", f); toast.success(data.message); onSaved(data.contractor); setF({ from: "", to: "", note: "" }); }
    catch (e) { err(e, "Couldn't add those dates"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { const { data } = await API.delete(`/marketplace/me/away/${id}`); toast.success(data.message); onSaved(data.contractor); } catch (e) { err(e, "Couldn't remove"); }
  };
  const st = contractor.away_status;
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="away-dates">
      <div className="flex items-start gap-2">
        <Airplane size={18} className="text-sky-600 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-bold font-[Chivo]">Away dates</h3>
          <p className="text-xs text-muted-foreground">Holiday or fully booked? Block out the dates and customers see “Away until …” on your listing before they request a quote. Requests still arrive — they just know to expect a wait. No re-approval needed.</p>
        </div>
        {st?.away && <span className="text-xs font-medium text-sky-700 dark:text-sky-300" data-testid="away-now">Away now until {fmt(st.until)}</span>}
      </div>
      {periods.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border text-sm" data-testid="away-list">
          {periods.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`away-row-${a.id}`}>
              <span className="font-medium">{fmt(a.from)} – {fmt(a.to)}</span>
              {a.note && <span className="text-muted-foreground text-xs">{a.note}</span>}
              {a.to < new Date().toISOString().slice(0, 10) && <span className="text-[11px] text-muted-foreground">past</span>}
              <button onClick={() => remove(a.id)} className="ml-auto text-muted-foreground hover:text-rose-600" title="Remove" data-testid={`away-remove-${a.id}`}><Trash size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid sm:grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-end">
        <label className="text-xs text-muted-foreground">From<Input type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} className="h-9 mt-1" data-testid="away-from-input" /></label>
        <label className="text-xs text-muted-foreground">To<Input type="date" value={f.to} min={f.from || undefined} onChange={(e) => setF({ ...f, to: e.target.value })} className="h-9 mt-1" data-testid="away-to-input" /></label>
        <label className="text-xs text-muted-foreground">Note (optional)<Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Annual leave" className="h-9 mt-1" data-testid="away-note-input" /></label>
        <Button size="sm" onClick={add} disabled={busy || !f.from || !f.to} data-testid="away-add-btn">{busy ? "Saving…" : "Block out"}</Button>
      </div>
    </section>
  );
}
